// eslint-disable-next-line no-unused-vars
import React from 'react';
import styled from '@emotion/styled';
import { arrayOf, bool, func, object, shape, string } from 'prop-types';
import { Loading } from '@carbon/react';
import { Package } from 'lucide-react';

import AddToCart from 'Components/AddToCart/AddToCart';
import Img from 'Containers/Image/Image';

const StyledProductCard = styled.article`
  box-sizing: border-box;
  width: 100%;
  min-height: 670px;
  padding: 45px 32px 80px;
  background: #f8f8f8;
  color: #3f3f3f;

  .category {
    margin: 0 0 36px;
    color: #17647d;
    font-size: 10px;
    line-height: 1.4;
  }

  .title {
    margin: 0 0 86px;
    color: #68645e;
    font-size: 18px;
    font-weight: 600;
    line-height: 1.2;
  }

  .body {
    display: grid;
    grid-template-columns: minmax(280px, 36%) minmax(0, 1fr);
    gap: 14px;
    align-items: start;
  }

  .media {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .image {
    width: 100%;
    max-width: 215px;
    height: 285px;
    object-fit: contain;
  }

  svg.image {
    stroke: #afaeae;
  }

  .addToCartContainer {
    margin-top: 86px;
  }

  .description {
    margin: -8px 0 0;
    font-size: 14px;
    line-height: 1.47;
  }

  .specs {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin: 0 0 8px;
    padding: 0;
    list-style: none;
  }

  .specItem {
    display: block;

    strong {
      margin-right: 4px;
    }
  }

  .descriptionSegment {
    display: block;
    margin-bottom: 8px;
  }

  .status {
    margin: 0;
    color: #68645e;
    font-size: 18px;
  }

  @media (max-width: 720px) {
    min-height: 0;
    padding: 30px 20px 45px;

    .title {
      margin-bottom: 45px;
    }

    .body {
      grid-template-columns: 1fr;
    }

    .addToCartContainer {
      margin-top: 35px;
    }
  }
`;

const getResponseSpecs = product => (
  product?.description ? [{ label: '', value: product.description }] : []
);

const getDisplaySpecs = (specs, product) => {
  const filledSpecs = (specs || [])
    .map(spec => ({
      label: String(spec?.label ?? ''),
      value: String(spec?.value ?? ''),
    }))
    .filter(spec => spec.label.trim() || spec.value.trim());

  return filledSpecs.length ? filledSpecs : getResponseSpecs(product);
};

const ProductCard2Component = ({
  product,
  loading,
  error,
  addingToCart,
  onAddToCart,
  title,
  specs,
}) => {
  if (loading) {
    return <StyledProductCard><p className="status">Se incarca produsul...</p></StyledProductCard>;
  }

  if (error || !product) {
    return <StyledProductCard><p className="status">Produsul nu a putut fi incarcat.</p></StyledProductCard>;
  }

  const displayTitle = title?.trim() || product.title;
  const displaySpecs = getDisplaySpecs(specs, product);

  return (
    <StyledProductCard>
      <p className="category">{product.categoryName}</p>
      <h2 className="title">{displayTitle}</h2>
      <div className="body">
        <div className="media">
          <Img
            src={ product.pictureUrl }
            className="image"
            unloader={ <Package size={ 50 } strokeWidth={ 1.25 } /> }
            loading="lazy"
            loader={ <Loading active={ loading } small withOverlay={ false } /> }
            alt={ product.name || displayTitle }
          />
          <AddToCart
            onClick={ onAddToCart }
            trackingContext={ {
              location: 'product_grid_item',
              product_sku: product?.code,
            } }
            showSelector={ false }
            loading={ addingToCart }
            disabled={
              addingToCart
              || product.warnings?.blocked_prices
              || product.warnings?.blocked_stocks
              || product.warnings?.blocked_orders
            }
            min={ Math.max(product.minQuantity, 1) }
            step={ Math.max(product.minQuantity, 1) }
            max={ product.stockValue }
            value={ product.quantity }
            id={ product.id }
            size="md"
          />
        </div>
        <div className="description">
          { displaySpecs.length ? (
            <ul className="specs">
              { displaySpecs.map(spec => (
                <li className="specItem" key={ `${spec.label}-${spec.value}` }>
                  { spec.label.trim() ? <strong>{spec.label}:</strong> : null }
                  {spec.value}
                </li>
              )) }
            </ul>
          ) : null }
        </div>
      </div>
    </StyledProductCard>
  );
};

ProductCard2Component.propTypes = {
  product: object,
  loading: bool,
  error: object,
  addingToCart: bool,
  onAddToCart: func,
  title: string,
  specs: arrayOf(shape({
    label: string,
    value: string,
  })),
};

export default ProductCard2Component;
